// src/hooks/useDraggableLabels.js
import { useEffect, useRef } from 'react';
import * as THREE from 'three';

const MAX_DISTANCE = 50;

const useDraggableLabels = (scene, camera, renderer) => {
  const draggingRef = useRef(null);
  const offsetRef = useRef(new THREE.Vector3());

  useEffect(() => {
    if (!scene || !camera || !renderer) return;
    const domElement = renderer.domElement;

    const getMouseNDC = (event) => {
      const rect = domElement.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      return new THREE.Vector2(x, y);
    };

    const getWorldPoint = (ndc) => {
      return new THREE.Vector3(ndc.x, ndc.y, 0).unproject(camera);
    };

    const onMouseDown = (event) => {
      const mouseNDC = getMouseNDC(event);
      const raycaster = new THREE.Raycaster();
      raycaster.setFromCamera(mouseNDC, camera);

      const intersects = raycaster.intersectObjects(scene.children, true);

      for (let hit of intersects) {
        const obj = hit.object;
        if (obj.userData?.type === 'groundingBuatLabel' && obj.userData?.parentBuat) {
          draggingRef.current = obj;

          const worldPoint = getWorldPoint(mouseNDC);
          offsetRef.current = new THREE.Vector3().subVectors(worldPoint, obj.position);
          break;
        }
      }
    };

    const onMouseMove = (event) => {
      if (!draggingRef.current) return;

      const label = draggingRef.current;
      const parent = label.userData.parentBuat;
      if (!parent) return;

      const ndc = getMouseNDC(event);
      const worldPoint = getWorldPoint(ndc);
      const newPos = new THREE.Vector3().subVectors(worldPoint, offsetRef.current);

      const center = parent.position;
      const distance = center.distanceTo(newPos);

      if (distance <= MAX_DISTANCE) {
        label.position.copy(newPos);
      } else {
        const dir = new THREE.Vector3().subVectors(newPos, center).normalize();
        label.position.copy(new THREE.Vector3().addVectors(center, dir.multiplyScalar(MAX_DISTANCE)));
      }
    };

    const onMouseUp = () => {
      draggingRef.current = null;
    };

    domElement.addEventListener('mousedown', onMouseDown);
    domElement.addEventListener('mousemove', onMouseMove);
    domElement.addEventListener('mouseup', onMouseUp);

    return () => {
      domElement.removeEventListener('mousedown', onMouseDown);
      domElement.removeEventListener('mousemove', onMouseMove);
      domElement.removeEventListener('mouseup', onMouseUp);
    };
  }, [scene, camera, renderer]);
};

export default useDraggableLabels;
